import { useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { dms } from "@/pages/dms/api";

/**
 * FieldGpsPinger — mounts inside DmsShell.
 * For EVERY role except the Company Owner: while the user is currently
 * PUNCHED IN, post the location every 60s so the Owner / TL / RM can see them
 * live on the map. Stops pinging once they punch out. Silent on error.
 *
 * Resilience: mobile browsers throttle/pause setInterval when the tab is
 * backgrounded. To reduce gaps, we also fire an immediate ping whenever the
 * tab/app becomes visible again (screen unlock, app switch back, etc).
 */
export default function SalespersonGpsPinger() {
  const { user, impersonation } = useAuth();
  const timerRef = useRef();
  const lastSentRef = useRef(0);

  useEffect(() => {
    const FIELD_ROLES = ["salesperson", "team_leader", "regional_manager"];
    if (!user || !FIELD_ROLES.includes(user.role)) return;
    if (impersonation) return;
    if (!("geolocation" in navigator)) return;

    let cancelled = false;

    const send = async (force) => {
      const now = Date.now();
      if (!force && now - lastSentRef.current < 20000) return;

      let punchedIn = false;
      try {
        const p = await dms.punchToday();
        punchedIn = !!p?.punched_in;
      } catch { punchedIn = false; }
      if (!punchedIn || cancelled) return;

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (cancelled) return;
          const { latitude, longitude, accuracy, speed } = pos.coords;
          lastSentRef.current = Date.now();
          dms.trackingPing({ lat: latitude, lng: longitude, accuracy, speed }).catch(() => {});
        },
        () => { /* permission denied / unavailable — ignore */ },
        { enableHighAccuracy: false, maximumAge: 30000, timeout: 8000 },
      );
    };

    send(true);
    timerRef.current = setInterval(() => send(false), 60_000);

    const onVisible = () => {
      if (document.visibilityState === "visible") send(true);
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    window.addEventListener("pageshow", onVisible);

    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.removeEventListener("pageshow", onVisible);
    };
  }, [user, impersonation]);

  return null;
}
